import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowLeft, ArrowRight, BedDouble, Bath, ShowerHead, Snowflake, Wifi, Tv,
    CookingPot, Waves, Flame, Mountain, Sofa, Droplets,
} from "lucide-react";
import { CHALES, brl } from "../data";
import { Reveal } from "./Reveal";

const ICONS = { BedDouble, Bath, ShowerHead, Snowflake, Wifi, Tv, CookingPot, Waves, Flame, Mountain, Sofa, Droplets };

function Gallery({ fotos, nome, testId }) {
    const [[index, dir], setIndex] = useState([0, 0]);
    const go = (d) => setIndex(([i]) => [(i + d + fotos.length) % fotos.length, d]);

    return (
        <div className="relative aspect-[4/3] overflow-hidden bg-stone-200 group" data-testid={`${testId}-gallery`}>
            <AnimatePresence custom={dir} initial={false}>
                <motion.img
                    key={index}
                    src={fotos[index]}
                    alt={`${nome} — foto ${index + 1}`}
                    custom={dir}
                    initial={{ x: dir >= 0 ? "100%" : "-100%", opacity: 0.4 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: dir >= 0 ? "-100%" : "100%", opacity: 0.4 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                />
            </AnimatePresence>
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 bg-gradient-to-t from-black/50 to-transparent">
                <span className="text-stone-100 text-xs font-bold tracking-[0.2em]" data-testid={`${testId}-counter`}>
                    {String(index + 1).padStart(2, "0")} / {String(fotos.length).padStart(2, "0")}
                </span>
                <div className="flex gap-2">
                    <button
                        data-testid={`${testId}-prev-button`}
                        onClick={() => go(-1)}
                        aria-label="Foto anterior"
                        className="h-11 w-11 flex items-center justify-center bg-stone-50/80 backdrop-blur-md text-stone-900 hover:bg-emerald-900 hover:text-stone-50 transition-colors"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <button
                        data-testid={`${testId}-next-button`}
                        onClick={() => go(1)}
                        aria-label="Próxima foto"
                        className="h-11 w-11 flex items-center justify-center bg-stone-50/80 backdrop-blur-md text-stone-900 hover:bg-emerald-900 hover:text-stone-50 transition-colors"
                    >
                        <ArrowRight size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Chales({ selected, onSelect }) {
    return (
        <section id="chales" data-testid="chales-section" className="py-24 md:py-32 bg-stone-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <Reveal>
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-800 mb-4">Nossos refúgios</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight max-w-2xl">
                        Dois chalés, um mesmo convite: desacelerar.
                    </h2>
                </Reveal>

                <div className="mt-20 md:mt-28 space-y-24 md:space-y-36">
                    {CHALES.map((chale, idx) => (
                        <div key={chale.id} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <Reveal className={idx % 2 === 1 ? "lg:order-2" : ""}>
                                <Gallery fotos={chale.fotos} nome={chale.nome} testId={`chale-${chale.id}`} />
                            </Reveal>
                            <Reveal delay={0.1} className={idx % 2 === 1 ? "lg:order-1" : ""}>
                                <span className="text-outline text-7xl md:text-8xl font-black leading-none select-none">
                                    {chale.capitulo}
                                </span>
                                <h3 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" data-testid={`chale-${chale.id}-name`}>
                                    {chale.nome}
                                </h3>
                                <p className="mt-2 text-emerald-800 text-sm font-bold uppercase tracking-widest">{chale.tagline}</p>
                                <p className="mt-5 text-base sm:text-lg leading-relaxed text-stone-600">{chale.descricao}</p>

                                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                                    {chale.features.map((f) => {
                                        const Icon = ICONS[f.icon];
                                        return (
                                            <div key={f.label} className="flex items-center gap-2.5 text-sm text-stone-700">
                                                <Icon size={16} className="text-emerald-800 shrink-0" />
                                                {f.label}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-10 flex flex-wrap items-center gap-6">
                                    <div data-testid={`chale-${chale.id}-price`}>
                                        <span className="text-3xl font-black tracking-tight text-stone-900">{brl(chale.diaria)}</span>
                                        <span className="text-stone-500 text-sm"> / diária</span>
                                    </div>
                                    <button
                                        data-testid={`chale-${chale.id}-select-button`}
                                        onClick={() => onSelect(chale.id)}
                                        className={`text-xs font-bold uppercase tracking-widest px-7 py-4 transition-colors duration-300 ${
                                            selected === chale.id
                                                ? "bg-emerald-900 text-stone-50"
                                                : "border border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-stone-50"
                                        }`}
                                    >
                                        {selected === chale.id ? "Selecionado — ir para reserva" : "Reservar este chalé"}
                                    </button>
                                </div>
                            </Reveal>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
